/* Cart display and informational admin reference; paid-order backend owns fulfillment. */
(() => {
  const pacificFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  const pacificParts = timestamp => {
    const parts = {};
    pacificFormatter.formatToParts(timestamp).forEach(part => {
      if (part.type !== "literal") parts[part.type] = Number(part.value);
    });
    return [parts.year, parts.month, parts.day, parts.hour, parts.minute];
  };
  // Resolve Pacific wall-clock time with the IANA timezone, including DST.
  // Invalid calendar dates and nonexistent spring-forward times fail closed.
  const parsePacificDateTime = value => {
    const match = typeof value === "string" &&
      /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) return NaN;
    const desired = match.slice(1).map(Number);
    const [year, month, day, hour, minute] = desired;
    if (month < 1 || month > 12 || day < 1 || day > 31 ||
        hour > 23 || minute > 59) return NaN;
    const wallTime = Date.UTC(year, month - 1, day, hour, minute);
    let timestamp = wallTime;
    for (let i = 0; i < 4; i++) {
      const [y, m, d, h, min] = pacificParts(timestamp);
      const adjustment = wallTime - Date.UTC(y, m - 1, d, h, min);
      if (adjustment === 0) break;
      timestamp += adjustment;
    }
    return pacificParts(timestamp).every((part, i) => part === desired[i])
      ? timestamp : NaN;
  };
  const active = (data, now = Date.now()) => {
    if (data?.editorEnabled !== "true" || data.consumerEligible !== "true") return false;
    const start = parsePacificDateTime(data.editorStart);
    const end = parsePacificDateTime(data.editorEnd);
    return Number.isFinite(start) && Number.isFinite(end) &&
      start < end && now >= start && now < end;
  };
  const copy = (value, fallback, token, replacement) => {
    const text = typeof value === "string" && value.trim() &&
      (!token || value.split(token).length === 2) ? value : fallback;
    return token ? text.split(token).join(replacement) : text;
  };
  const state = cents => {
    if (!Number.isInteger(cents) || cents < 0) return null;
    const award = cents >= 15000 ? 30 : cents >= 13000 ? 20 : 0;
    const dollars = n =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: n % 100 ? 2 : 0,
      }).format(n / 100);
    return {
      award,
      cardTier: award >= 20 ? 30 : 20,
      percent: Math.min((cents / 15000) * 100, 100),
      title: `FREE $${award === 30 ? 30 : 20} Digital Gift Card`,
      emphasis:
        award < 30 && cents > 0
          ? `${dollars((award === 20 ? 15000 : 13000) - cents)} more`
          : "",
      message:
        award === 30
          ? "We’ll email it to you after purchase."
          : award === 20
            ? `Add ${dollars(15000 - cents)} more to make it a $30 gift card!`
            : cents === 0
              ? "Spend $130 to unlock your FREE gift card."
              : `Add ${dollars(13000 - cents)} more to unlock your FREE gift card.`,
    };
  };
  // Private order-admin reference only. Never a gift-card qualification input.
  const referenceAttributes = {
    tier: "__gwp_oct2026_tier",
    log: "__gwp_oct2026_log",
  };
  const seenFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles", month: "short", day: "numeric",
    year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  const displayReference = (cents, currency, timestamp = Date.now()) => {
    const result = state(cents);
    if (!result?.award || currency !== "USD") return null;
    return {
      [referenceAttributes.tier]: result.award === 20 ? "1" : "2",
      [referenceAttributes.log]: `subtotal=${(cents / 100).toFixed(2)} USD; gift_card=${result.award} USD; seen_at=${seenFormatter.format(timestamp)}`,
    };
  };
  // One serialized, debounced writer per cart survives section replacement.
  // Send only these keys; never replace other attributes, items, notes or discounts.
  const referenceStates = new Map();
  const queueReference = (url, attributes) => {
    if (!url || !attributes) return;
    if (!referenceStates.has(url)) referenceStates.set(url, {});
    const ref = referenceStates.get(url);
    const value = JSON.stringify(attributes);
    if (ref.desired === value) return;
    ref.desired = value;
    ref.attributes = attributes;
    ref.failed = undefined;
    clearTimeout(ref.timer);
    const flush = async () => {
      if (ref.inFlight || ref.desired === ref.sent || ref.desired === ref.failed) return;
      const snapshot = ref.desired;
      ref.inFlight = true;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: JSON.parse(snapshot) }),
        });
        if (!response.ok) throw Error("reference");
        ref.sent = snapshot;
      } catch {
        ref.sent = undefined;
        ref.failed = snapshot; // Retry only after another distinct display.
      } finally {
        ref.inFlight = false;
        if (ref.desired !== snapshot) {
          clearTimeout(ref.timer);
          ref.timer = setTimeout(flush, 200);
        }
      }
    };
    ref.timer = setTimeout(flush, 200);
  };
  // Document-lifetime state survives section replacement without customer storage.
  const motionStates = new Map();
  const transitionTier = (motion, award) => {
    if (motion.award === award) return false;
    const previous = motion.award;
    motion.award = award;
    motion.revision++;
    motion.pending = previous !== null && award > previous && award > 0
      ? { award, revision: motion.revision } : null;
    return true;
  };
  class GiftCardCart extends HTMLElement {
    connectedCallback() {
      this.generation = 0;
      this.onChange = () => this.refresh();
      this.onVisibility = () => this.queueAudit();
      this.onOpen = () => { this.queueGleam(); this.queueAudit(); };
      this.onClose = () => {
        this.stopGleam();
        this.stopAudit();
        if (this.motion) this.motion.pending = null;
      };
      this.motionPreference = matchMedia("(prefers-reduced-motion: reduce)");
      this.onMotionChange = () => {
        if (this.motionPreference.matches) this.onClose();
      };
      document.addEventListener("theme:cart:change", this.onChange);
      document.addEventListener("visibilitychange", this.onVisibility);
      document.addEventListener("theme:cart-drawer:open", this.onOpen);
      document.addEventListener("theme:cart-drawer:close", this.onClose);
      this.motionPreference.addEventListener("change", this.onMotionChange);
      this.visibilityObserver = new IntersectionObserver(() => this.queueAudit(), { threshold: 1 });
      this.visibilityObserver.observe(this);
      this.load();
    }
    disconnectedCallback() {
      document.removeEventListener("theme:cart:change", this.onChange);
      document.removeEventListener("visibilitychange", this.onVisibility);
      document.removeEventListener("theme:cart-drawer:open", this.onOpen);
      document.removeEventListener("theme:cart-drawer:close", this.onClose);
      this.motionPreference?.removeEventListener("change", this.onMotionChange);
      if (this.eligibilityNote) this.eligibilityNote.hidden = true;
      this.visibilityObserver?.disconnect();
      this.stopAudit();
      this.stopGleam();
      clearInterval(this.timer);
      this.generation++;
    }
    load() {
      if (!this.isConnected) return;
      this.render(
        this.cents ?? Number(this.dataset.subtotal),
        this.currency ?? this.dataset.currency
      );
      const previous = referenceStates.get(this.dataset.attributeUrl)?.attributes;
      if (previous) this.syncReferenceInputs(previous);
      this.timer = setInterval(
        () => this.render(this.cents, this.currency),
        30000
      );
    }
    async refresh() {
      const generation = ++this.generation;
      try {
        const cart = await fetch(this.dataset.cartUrl, {
          cache: "no-store",
        }).then(r => {
          if (!r.ok) throw Error("cart");
          return r.json();
        });
        if (generation === this.generation && this.isConnected) {
          if (cart.taxes_included === true) this.dataset.taxExclusive = "false";
          this.render(cart.total_price, cart.currency);
        }
      } catch {
        if (generation === this.generation) this.hide();
      }
    }
    render(cents, currency) {
      this.cents = cents;
      this.currency = currency;
      const result = state(cents);
      this.hidden = !(
        active(this.dataset) &&
        this.dataset.taxExclusive === "true" &&
        currency === "USD" &&
        result
      );
      if (this.hidden) {
        this.hide();
        return;
      }
      result.title = result.award
        ? copy(result.award === 30 ? this.dataset.confirmation30Copy : this.dataset.confirmationCopy,
            result.award === 30 ? "You earned a $30 gift card!" : "FREE $20 gift card unlocked!",
            "{award}", `$${result.award}`).replace(/[.!*\s]+$/, "") + "!"
        : copy(this.dataset.titleCopy, result.title, "{award}", "$20");
      result.message = result.award === 30
        ? copy(this.dataset.unlockedCopy, result.message)
        : cents === 0
          ? copy(this.dataset.emptyCopy, result.message)
          : copy(result.award === 20 ? this.dataset.upgradeCopy : this.dataset.qualifyCopy,
              result.message, "{amount_more}", result.emphasis);
      if (result.award) result.message = result.message.trim().replace(/\*+$/, "") + "*";
      this.dataset.award = String(result.award);
      this.dataset.cardTier = String(result.cardTier);
      const title = this.querySelector(".bgc__default-title");
      title.hidden = result.award > 0;
      if (!result.award && title.textContent !== result.title) title.textContent = result.title;
      const [before, remainder = ""] = result.emphasis
        ? result.message.split(result.emphasis)
        : [result.message];
      const upgrade = result.award === 20 && remainder.includes("$30 gift card!")
        ? "$30 gift card!" : "";
      const [after, end = ""] = upgrade ? remainder.split(upgrade) : [remainder];
      this.querySelector(".bgc__message-before").textContent = before;
      this.querySelector(".bgc__message-emphasis").textContent = result.emphasis;
      this.querySelector(".bgc__message-after").textContent = after;
      this.querySelector(".bgc__message-upgrade").textContent = upgrade;
      this.querySelector(".bgc__message-end").textContent = end;
      this.querySelectorAll("[data-tier]").forEach(el => {
        const earned = Number(el.dataset.tier) === result.award;
        el.classList.toggle("is-earned", earned);
        el.querySelector(".bgc__tier-label").textContent = earned
          ? `Qualifies for $${result.award} gift card`
          : `Spend $${el.dataset.tier === "20" ? 130 : 150} · $${el.dataset.tier} gift card`;
      });
      const confirmation = this.querySelector(".bgc__qualified");
      confirmation.hidden = result.award === 0;
      this.querySelector(".bgc__qualified-copy").textContent = result.award ? result.title : "";
      this.syncEligibility(result.award > 0);
      if (!result.award) this.recordReference(null);
      const bar = this.querySelector('[role="progressbar"]');
      bar.setAttribute("aria-valuenow", String(Math.min(cents / 100, 150)));
      bar.setAttribute("aria-valuetext", `$${(Math.min(cents, 15000) / 100).toFixed(2)} of $150 merchandise subtotal`);
      bar.querySelector("span").style.width = result.percent + "%";
      const key = "bioray-october-2026:" + this.dataset.cartUrl;
      if (!motionStates.has(key))
        motionStates.set(key, {
          // Preserve a cart transition against the server-rendered subtotal.
          award: state(Number(this.dataset.subtotal))?.award ?? result.award,
          revision: 0, pending: null,
        });
      this.motion = motionStates.get(key);
      if (transitionTier(this.motion, result.award)) this.stopGleam();
      this.queueGleam();
      this.queueAudit();
    }
    syncEligibility(earned) {
      const note = this.closest("cart-drawer")?.querySelector("[data-giftcard-eligibility]");
      if (this.eligibilityNote && this.eligibilityNote !== note)
        this.eligibilityNote.hidden = true;
      this.eligibilityNote = note;
      if (note) note.hidden = !earned;
      const title = this.querySelector(".bgc__title");
      if (earned && note?.id) title.setAttribute("aria-describedby", note.id);
      else title.removeAttribute("aria-describedby");
    }
    hide() {
      this.hidden = true;
      this.syncEligibility(false);
      this.recordReference(null);
      this.stopAudit();
      this.stopGleam();
      if (this.motion) this.motion.pending = null;
    }
    syncReferenceInputs(attributes) {
      const drawer = this.closest("cart-drawer");
      for (const field of ["tier", "log"]) {
        const input = drawer?.querySelector(`[data-giftcard-display-${field}]`);
        if (input) { input.value = attributes[referenceAttributes[field]]; input.disabled = false; }
      }
    }
    recordReference(snapshot) {
      const url = this.dataset.attributeUrl;
      if (!url) return;
      const key = snapshot ? `${this.cents}:${this.currency}` : "";
      const previous = referenceStates.get(url);
      // Keep the first visible timestamp until tier/subtotal meaningfully changes.
      if (previous?.displayKey === key) {
        this.syncReferenceInputs(previous.attributes);
        return;
      }
      const attributes = snapshot || {
        [referenceAttributes.tier]: "", [referenceAttributes.log]: "",
      };
      this.syncReferenceInputs(attributes);
      queueReference(url, attributes);
      referenceStates.get(url).displayKey = key;
    }
    stopAudit() {
      if (this.auditFrame) cancelAnimationFrame(this.auditFrame);
      this.auditFrame = null;
    }
    queueAudit() {
      const drawer = this.closest("cart-drawer");
      if (!this.dataset.attributeUrl || this.auditFrame || this.hidden ||
          !this.isConnected || !drawer?.classList.contains("is-open")) return;
      const startedAt = Date.now();
      const check = () => {
        this.auditFrame = null;
        if (this.hidden || !this.isConnected || !active(this.dataset) ||
            !drawer.classList.contains("is-open")) return;
        const rect = this.getBoundingClientRect();
        let visible = document.visibilityState === "visible" &&
          rect.width > 0 && rect.height > 0 && rect.left >= 0 &&
          rect.right <= innerWidth && rect.top >= 0 && rect.bottom <= innerHeight;
        for (let node = this; node && visible; node = node.parentElement) {
          const style = getComputedStyle(node);
          visible = style.display !== "none" && style.visibility === "visible" &&
            Number(style.opacity) >= 0.99;
          if (node === drawer) break;
        }
        if (visible) {
          const snapshot = displayReference(this.cents, this.currency);
          if (snapshot) this.recordReference(snapshot);
        } else if (Date.now() - startedAt <= 5000) {
          this.auditFrame = requestAnimationFrame(check);
        }
      };
      this.auditFrame = requestAnimationFrame(check);
    }
    stopGleam() {
      if (this.gleamFrame) cancelAnimationFrame(this.gleamFrame);
      clearTimeout(this.gleamCleanup);
      if (this.gleamArt) {
        this.gleamArt.classList.remove("is-gleaming");
        this.gleamArt.removeEventListener("animationend", this.onGleamEnd);
      }
      this.gleamArt = null;
      this.gleamFrame = null;
      this.waitingRevision = null;
    }
    queueGleam() {
      const pending = this.motion?.pending;
      if (!pending || this.waitingRevision === pending.revision) return;
      this.stopGleam();
      if ((this.motionPreference || matchMedia("(prefers-reduced-motion: reduce)")).matches) {
        this.motion.pending = null;
        return;
      }
      this.waitingRevision = pending.revision;
      const startedAt = Date.now();
      let settledFrames = 0;
      const check = () => {
        this.gleamFrame = null;
        if (!this.isConnected || this.hidden || this.motion.pending !== pending ||
            this.motion.award !== pending.award) {
          this.stopGleam();
          return;
        }
        const drawer = this.closest("cart-drawer");
        if (drawer && !drawer.classList.contains("is-open")) {
          this.stopGleam();
          return; // A newly earned tier can wait for its first drawer opening.
        }
        const root = drawer || this.parentElement || this;
        const busy = root.getAnimations({ subtree: true }).some(animation => {
          const target = animation.effect?.target;
          return !this.contains(target) &&
            target?.contains(this) &&
            Number.isFinite(animation.effect?.getComputedTiming().endTime) &&
            (animation.pending || animation.playState === "running" || animation.playState === "paused");
        });
        // Gleam follows the visible next-tier artwork; earning still tracks award.
        const art = this.querySelector(`[data-tier="${this.dataset.cardTier}"]`);
        const image = art.querySelector("img");
        const rect = art.getBoundingClientRect();
        let visible = rect.width > 0 && rect.height > 0 && rect.right > 0 &&
          rect.left < innerWidth && rect.bottom > 0 && rect.top < innerHeight;
        for (let node = this; node && visible; node = node.parentElement) {
          const style = getComputedStyle(node);
          visible = style.display !== "none" && style.visibility === "visible" &&
            Number(style.opacity) >= 0.99;
          if (node === root) break;
        }
        settledFrames = !busy && visible && image.complete && image.naturalWidth > 0
          ? settledFrames + 1 : 0;
        if (settledFrames >= 2) {
          this.motion.pending = null;
          this.waitingRevision = null;
          this.gleamArt = art;
          this.onGleamEnd = event => {
            if (event.target === art && event.animationName === "bgc-gleam") this.stopGleam();
          };
          art.addEventListener("animationend", this.onGleamEnd);
          art.classList.add("is-gleaming");
          this.gleamCleanup = setTimeout(() => this.stopGleam(), 600);
          return;
        }
        if (Date.now() - startedAt > 5000) {
          this.motion.pending = null;
          this.stopGleam();
          return;
        }
        this.gleamFrame = requestAnimationFrame(check);
      };
      // Cart build dispatches change before animateItems' rAF; two settled frames
      // and current Animation objects cover its later fade plus drawer opening.
      this.gleamFrame = requestAnimationFrame(check);
    }
  }
  if (!customElements.get("bioray-giftcard-cart"))
    customElements.define("bioray-giftcard-cart", GiftCardCart);
  if (typeof module !== "undefined")
    module.exports = { active, state, transitionTier, parsePacificDateTime, copy, displayReference, referenceAttributes, queueReference };
})();
